import React, { useState, useRef, useEffect } from 'react';
import { FiSave, FiPlus, FiTrash2, FiImage, FiX, FiLayout, FiGrid } from 'react-icons/fi';
import ImagePreview from './common/ImagePreview';
import api from '../../api';

const GallerySection = ({
  websiteData,
  handleInputChange,
  handleSave,
  tenantId
}) => {
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notify, setNotify] = useState({ visible: false, type: '', message: '' });
  const [saveStatus, setSaveStatus] = useState('');
  const [deleteModal, setDeleteModal] = useState({ isOpen: false, imageIndex: null, imageName: '' });
  const [localGalleryData, setLocalGalleryData] = useState(websiteData.gallery || {
    isActive: false,
    sectionTitle: 'Campus Life',
    sectionSubtitle: 'A glimpse into our vibrant community',
    layout: 'grid',
    columns: 4,
    items: []
  });
  const fileInputRef = useRef(null);

  // Sync local state with prop changes
  useEffect(() => {
    if (websiteData?.gallery) {
      setLocalGalleryData(websiteData.gallery);
    }
  }, [websiteData?.gallery]);

  // Notification timeout
  useEffect(() => {
    if (!notify.visible) return;
    const timer = setTimeout(() => setNotify({ visible: false, type: '', message: '' }), 3000);
    return () => clearTimeout(timer);
  }, [notify.visible]);

  // Show notification
  const showNotification = (type, message) => {
    setNotify({ visible: true, type, message });
  };

  // Open delete confirmation modal
  const openDeleteModal = (index) => {
    const image = localGalleryData?.items?.[index];
    const imageName = (image?.caption && image.caption.trim()) ? image.caption : (image?.filename || `Image ${index + 1}`);
    setDeleteModal({ isOpen: true, imageIndex: index, imageName });
  };

  // Close delete modal
  const closeDeleteModal = () => {
    setDeleteModal({ isOpen: false, imageIndex: null, imageName: '' });
  };

  // Handle manual save
  const handleManualSave = async () => {
    setSaving(true);
    try {
      const resp = await api.put(`/api/website/${tenantId}/gallery`, localGalleryData);
      if (!resp?.data?.success) {
        throw new Error(resp?.data?.message || 'Failed to save gallery');
      }
      showNotification('success', 'Gallery saved successfully');
      handleInputChange('gallery', null, localGalleryData);
      const now = new Date();
      const ts = now.toLocaleTimeString();
      setSaveStatus(`Saved ${ts}`);
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving gallery:', error);
      showNotification('error', error?.message || 'Failed to save gallery');
    } finally {
      setSaving(false);
    }
  };

  // Update local gallery data
  const updateLocalGallery = (field, value) => {
    setLocalGalleryData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Update image caption
  const updateImageCaption = (index, caption) => {
    const newItems = [...(localGalleryData?.items || [])];
    newItems[index] = { ...newItems[index], caption };
    updateLocalGallery('items', newItems);
  };

  // Add gallery images
  const addGalleryImage = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !tenantId) return;

    const currentItems = localGalleryData?.items || [];
    const totalAfterUpload = currentItems.length + files.length;
    if (totalAfterUpload > 10) {
      showNotification('error', `Maximum 10 images allowed. You have ${currentItems.length} images and trying to add ${files.length} more.`);
      event.target.value = '';
      return;
    }

    try {
      setUploading(true);
      
      const formData = new FormData();
      Array.from(files).forEach(file => {
        formData.append('images', file);
      });

      const response = await api.post(
        `/api/website/${tenantId}/gallery/upload`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      if (response.data.success) {
        const uploadedImages = response.data.data.map(item => ({
          fileId: item.fileId || item._id,
          url: item.downloadUrl || item.url,
          filename: item.filename,
          caption: '',
          altText: '',
          isActive: true,
          order: currentItems.length + 1,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }));

        const newItems = [...currentItems, ...uploadedImages];
        updateLocalGallery('items', newItems);
        showNotification('success', `Successfully uploaded ${files.length} image(s)`);
      } else {
        showNotification('error', response.data.message || 'Upload failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      showNotification('error', error.response?.data?.message || 'Failed to upload images');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  // Remove gallery image
  const removeGalleryImage = async () => {
    const { imageIndex } = deleteModal;
    if (imageIndex === null) return;

    try {
      const imageToRemove = localGalleryData?.items?.[imageIndex];
      if (!imageToRemove) return;

      const response = await api.delete(`/api/website/${tenantId}/gallery/image`, { 
        data: { imageUrl: imageToRemove.url } 
      });

      if (response.data.success) {
        const newItems = (localGalleryData?.items || []).filter((_, i) => i !== imageIndex);
        updateLocalGallery('items', newItems);
        showNotification('success', 'Image deleted successfully');
      } else {
        throw new Error(response.data.message || 'Failed to delete image');
      }
    } catch (error) {
      console.error('Error removing image:', error);
      showNotification('error', error.response?.data?.message || error.message || 'Failed to remove image');
    } finally {
      closeDeleteModal();
    }
  };

  // Trigger file input
  const triggerFileInput = () => {
    const currentItems = localGalleryData?.items || [];
    if (currentItems.length >= 10) {
      showNotification('error', 'Maximum 10 images allowed in gallery');
      return;
    }
    fileInputRef.current?.click();
  };

  // Toggle gallery active status
  const toggleActive = () => {
    const newValue = !localGalleryData?.isActive;
    updateLocalGallery('isActive', newValue);
  };

  // Update gallery settings
  const updateSetting = (field, value) => {
    updateLocalGallery(field, value);
  };

  return (
    <>
      <div className="mt-2 grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* Settings & Configuration Core */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
            {/* Header Toolbar */}
            <div className="px-5 py-4 border-b border-slate-150 bg-white flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="text-base font-bold text-slate-900">Media Gallery Layout</h3>
                <p className="text-xs text-slate-500 mt-0.5">Manage your core system dashboard gallery viewmaps.</p>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={toggleActive}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    localGalleryData?.isActive !== false
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100'
                      : 'bg-slate-100 text-slate-600 border border-slate-200 hover:bg-slate-200'
                  }`}
                >
                  <span className={`h-1.5 w-1.5 rounded-full ${localGalleryData?.isActive !== false ? 'bg-emerald-600' : 'bg-slate-400'}`}></span>
                  {localGalleryData?.isActive !== false ? 'Active' : 'Inactive'}
                </button>

                {notify.visible && (
                  <span className={`px-2.5 py-1.5 rounded-lg text-xs font-medium border ${
                    notify.type === 'success' 
                      ? 'bg-emerald-50 text-emerald-800 border-emerald-200' 
                      : 'bg-rose-50 text-rose-800 border-rose-200'
                  }`}>
                    {notify.message}
                  </span>
                )}

                {saveStatus && (
                  <span className="px-2.5 py-1.5 rounded-lg text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-100">
                    {saveStatus}
                  </span>
                )}

                <button
                  onClick={handleManualSave}
                  disabled={saving}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg shadow-xs text-white transition-all ${
                    saving 
                      ? 'bg-slate-300 cursor-not-allowed' 
                      : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-offset-1 focus:ring-blue-500'
                  }`}
                >
                  <FiSave className="h-3.5 w-3.5" />
                  {saving ? 'Saving...' : 'Save Gallery'}
                </button>
              </div>
            </div>

            <div className="p-5 space-y-6">
              {/* Configuration Inputs */}
              <div className="bg-slate-50/70 rounded-xl p-4 border border-slate-200 space-y-4">
                <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                  <FiLayout className="text-blue-500 h-4 w-4" />
                  Visual Group Configurations
                </h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Header Title</label>
                    <input
                      type="text"
                      value={localGalleryData?.sectionTitle || 'Campus Life'}
                      onChange={(e) => updateSetting('sectionTitle', e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="Campus Life"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5">Section Sub-header Text</label>
                    <input
                      type="text"
                      value={localGalleryData?.sectionSubtitle || 'A glimpse into our vibrant community'}
                      onChange={(e) => updateSetting('sectionSubtitle', e.target.value)}
                      className="w-full text-sm px-3 py-2 border border-slate-200 rounded-lg bg-white focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      placeholder="A glimpse into our vibrant community"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                  <div>
                    <label className="block text-xs font-semibold text-slate-600 mb-2">Display Style Mode</label>
                    <div className="flex gap-3">
                      <button
                        type="button"
                        onClick={() => updateSetting('layout', 'grid')}
                        className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-xs font-medium transition-all flex-1 ${
                          localGalleryData?.layout === 'grid'
                            ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <FiGrid className="h-4 w-4 flex-shrink-0" />
                        <span>Responsive Grid</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => updateSetting('layout', 'carousel')}
                        className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-xs font-medium transition-all flex-1 ${
                          localGalleryData?.layout === 'carousel'
                            ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                        }`}
                      >
                        <FiLayout className="h-4 w-4 flex-shrink-0" />
                        <span>Slide Carousel</span>
                      </button>
                    </div>
                  </div>

                  {localGalleryData?.layout === 'grid' && (
                    <div>
                      <label htmlFor="grid-columns" className="block text-xs font-semibold text-slate-600 mb-1.5">
                        Desktop Grid Columns count
                      </label>
                      <select
                        id="grid-columns"
                        value={localGalleryData?.columns || 4}
                        onChange={(e) => updateSetting('columns', parseInt(e.target.value))}
                        className="w-full text-sm px-3 py-2 border border-slate-200 bg-white rounded-lg focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                      >
                        <option value="2">2 Columns dynamic width</option>
                        <option value="3">3 Columns structured width</option>
                        <option value="4">4 Columns standard layout</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Grid Array Images list */}
              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-4 py-3 bg-slate-50/50 border-b border-slate-200 flex items-center justify-between">
                  <h4 className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                    Gallery Assets Set <span className="font-normal normal-case text-slate-500 ml-1">({localGalleryData?.items?.length || 0} of 10 slots filled)</span>
                  </h4>
                  
                  <button
                    onClick={triggerFileInput}
                    disabled={uploading || (localGalleryData?.items?.length || 0) >= 10}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg shadow-xs text-white transition-all ${
                      uploading || (localGalleryData?.items?.length || 0) >= 10
                        ? 'bg-slate-300 cursor-not-allowed' 
                        : 'bg-blue-600 hover:bg-blue-700'
                    }`}
                  >
                    {uploading ? (
                      <>
                        <div className="animate-spin rounded-full h-3 w-3 border-2 border-white border-t-transparent"></div>
                        <span>Uploading...</span>
                      </>
                    ) : (
                      <>
                        <FiPlus className="h-3.5 w-3.5" />
                        <span>Add Media files</span>
                      </>
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={addGalleryImage}
                    className="hidden"
                    multiple
                    disabled={uploading || (localGalleryData?.items?.length || 0) >= 10}
                  />
                </div>

                <div className="p-4">
                  {localGalleryData?.items && localGalleryData.items.length > 0 ? (
                    <div className={`grid gap-4 ${
                      localGalleryData?.layout === 'grid' 
                        ? 'grid-cols-1 md:grid-cols-2'
                        : 'grid-cols-1'
                    }`}>
                      {localGalleryData.items.map((image, index) => (
                        <div key={index} className="group bg-white rounded-xl border border-slate-200 overflow-hidden shadow-xs hover:border-slate-300 transition-all flex flex-col">
                          <div className="relative bg-slate-100 aspect-video w-full overflow-hidden">
                            <ImagePreview
                              src={image.url}
                              alt={`Gallery item ${index + 1}`}
                              className="w-full h-full object-cover group-hover:scale-101 transition-transform duration-300"
                            />
                            <div className="absolute top-2 right-2 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                              <button
                                type="button"
                                onClick={() => openDeleteModal(index)}
                                className="p-2 bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors shadow-md"
                                title="Remove asset file"
                              >
                                <FiTrash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                          
                          <div className="p-3 bg-white border-t border-slate-100 flex-1 flex flex-col justify-end">
                            <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">Image Description Tag (Caption)</label>
                            <input
                              type="text"
                              value={image.caption || ''}
                              onChange={(e) => updateImageCaption(index, e.target.value)}
                              placeholder="Write an informational label context..."
                              className="w-full text-xs px-2.5 py-1.5 border border-slate-200 rounded-md focus:outline-hidden focus:ring-1 focus:ring-blue-500 focus:border-blue-500 transition-all text-slate-700"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-10 px-4 border-2 border-dashed border-slate-200 bg-slate-50/50 rounded-xl">
                      <div className="h-10 w-10 bg-slate-100 border border-slate-200 text-slate-400 rounded-xl flex items-center justify-center mx-auto mb-3">
                        <FiImage className="h-5 w-5" />
                      </div>
                      <h4 className="text-sm font-bold text-slate-800">No media assets mapped yet</h4>
                      <p className="text-xs text-slate-500 mt-1 max-w-xs mx-auto leading-relaxed">
                        Upload standard graphic components to build your page system. (JPG, PNG, WebP supported)
                      </p>
                      <button
                        type="button"
                        onClick={triggerFileInput}
                        className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-lg shadow-xs text-white bg-blue-600 hover:bg-blue-700 transition-all"
                      >
                        <FiPlus className="h-3.5 w-3.5" />
                        <span>Upload original image bundle</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Live System Preview Frame */}
        <div className="lg:col-span-1">
          <div className="bg-white border border-slate-200 rounded-2xl p-4 shadow-xs space-y-5 lg:sticky lg:top-24">
            <div>
              <h3 className="text-sm font-bold text-slate-900">Live Status Frame</h3>
              <p className="text-xs text-slate-400 mt-0.5">Real-time parameters checklist summary map tracker</p>
            </div>

            {/* Quick Map Meta tags details */}
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5 space-y-2.5 text-xs text-slate-700">
              <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                <span className="font-medium text-slate-500">Live Web Blueprint:</span>
                <span className={`font-semibold ${localGalleryData?.isActive !== false ? 'text-emerald-600' : 'text-slate-500'}`}>
                  {localGalleryData?.isActive !== false ? 'Active Layout' : 'Disabled / Closed'}
                </span>
              </div>
              <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                <span className="font-medium text-slate-500">Render Style Mode:</span>
                <span className="font-semibold capitalize text-slate-900">{localGalleryData?.layout || 'grid'}</span>
              </div>
              {localGalleryData?.layout === 'grid' && (
                <div className="flex items-center justify-between border-b border-slate-200/60 pb-2">
                  <span className="font-medium text-slate-500">Desktop View Grid:</span>
                  <span className="font-semibold text-slate-900">{localGalleryData?.columns || 4} Cols wide</span>
                </div>
              )}
              <div className="flex items-center justify-between">
                <span className="font-medium text-slate-500">Mapped Elements:</span>
                <span className="font-semibold text-slate-900">{localGalleryData?.items?.length || 0} / 10 Assets</span>
              </div>
            </div>

            {/* Real View Graphic Render */}
            <div className="bg-slate-900 rounded-xl p-4 border border-slate-800">
              <h4 className="text-[10px] font-bold text-slate-400 uppercase tracking-wider text-center mb-3">Live Visual Map Frame</h4>
              
              {localGalleryData?.items && localGalleryData.items.length > 0 ? (
                <div className={`grid gap-2 ${
                  localGalleryData?.layout === 'grid' 
                    ? 'grid-cols-2'
                    : 'grid-cols-1'
                }`}>
                  {localGalleryData.items.slice(0, 4).map((image, index) => (
                    <div key={index} className="bg-slate-800 rounded-lg overflow-hidden border border-slate-700 flex flex-col">
                      <div className="h-16 w-full bg-slate-950 overflow-hidden">
                        <ImagePreview
                          src={image.url}
                          alt={`Preview block ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      {image.caption && (
                        <div className="p-1.5 bg-slate-850 border-t border-slate-700/50">
                          <p className="text-slate-300 text-[10px] font-medium truncate">
                            {image.caption}
                          </p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <FiImage className="text-slate-600 h-6 w-6 mx-auto mb-1.5" />
                  <p className="text-xs text-slate-500">No layout asset values available</p>
                </div>
              )}

              {localGalleryData?.items && localGalleryData.items.length > 4 && (
                <p className="mt-3 text-center text-[11px] font-medium text-slate-400 bg-slate-800/40 py-1 rounded-md border border-slate-700/30">
                  + {localGalleryData.items.length - 4} additional items queued
                </p>
              )}
            </div>

            {/* Metric Data Stats counter fields */}
            <div className="bg-slate-50 border border-slate-150 rounded-xl p-3.5 space-y-2 text-xs text-slate-600">
              <div className="flex justify-between">
                <span>Free slots left:</span>
                <span className="font-semibold text-slate-900">{10 - (localGalleryData?.items?.length || 0)} available</span>
              </div>
              <div className="flex justify-between">
                <span>Items with descriptions:</span>
                <span className="font-semibold text-slate-900">
                  {(localGalleryData?.items || []).filter(img => img.caption?.trim()).length} records
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Overlay Pop-up Modal */}
      {deleteModal.isOpen && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs flex items-center justify-center p-4 z-50 animate-fade-in">
          <div className="bg-white rounded-2xl max-w-sm w-full p-5 shadow-xl border border-slate-100 transform scale-100 transition-all animate-scale-up">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-bold text-slate-900">Confirm file removal</h3>
              <button
                type="button"
                onClick={closeDeleteModal}
                className="p-1 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-50 transition-colors"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>
            
            <div className="space-y-3">
              <p className="text-xs text-slate-500 leading-relaxed">
                Are you completely sure you want to remove this image? This action will drop the database row immediately.
              </p>
              <div className="bg-rose-50 border border-rose-100 rounded-xl p-2.5 text-xs text-rose-800 font-medium truncate">
                Asset Name: {deleteModal.imageName}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={closeDeleteModal}
                className="px-3 py-1.5 text-xs font-semibold border border-slate-200 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
              >
                Go back
              </button>
              <button
                type="button"
                onClick={removeGalleryImage}
                className="px-3 py-1.5 text-xs font-semibold bg-rose-600 text-white rounded-lg hover:bg-rose-700 transition-colors shadow-xs"
              >
                Delete Asset
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default GallerySection;